import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import DaysAmount from '../../../common/DaysAmount';
import { useDispatch } from 'react-redux';
import {
  changeAdditionalFeeAmount,
  removeAdditionalFee,
} from '@redux/actions/basketActions';
import {
  changeAdditionalFeeAmountInLocalStorage,
  deleteAdditionalFeeFromLocalStorage,
} from '../helpers/basketHelpers';
import { AnimatePresence, motion } from 'framer-motion';
import { parsePrice } from '../../../../utils/index';
import EmptyImageIcon from '../../../../assets/icons/emptyImageIcon';
import { checkIsSalesManagoEnabled } from '@salesManago/helpers';
import { salesManagoUpdateCart } from '@salesManago/events/shopCart';
import { getBasket, getPromoCode } from '@redux/selectors/basketSelector';
import { getSelectedCity } from '@redux/selectors/summaryReducer.selectors';
import { getServerConfig } from '@features/general/generalSlice';
import { useAppSelector } from '@config/hooks';
import { classNamesUtil } from 'utilsRoot/classNamesUtil.util';

const ProductBasket = ({ additionalFeeList }) => {
  const dispatch = useDispatch();
  const { code } = useAppSelector(getPromoCode) || {};
  const selectedCity = useAppSelector(getSelectedCity);
  const basket = useAppSelector(getBasket);
  const { companyId, salesManago, serverUrl } = useAppSelector(getServerConfig);

  const handleRemoveClick = product => {
    dispatch(removeAdditionalFee(product));
    deleteAdditionalFeeFromLocalStorage(product.possibleSideOrderId);

    if (checkIsSalesManagoEnabled(salesManago) && basket?.length) {
      const feesList = [...additionalFeeList].filter(
        item => item.possibleSideOrderId !== product.possibleSideOrderId
      );

      salesManagoUpdateCart(
        basket[0],
        code,
        selectedCity,
        feesList,
        serverUrl,
        salesManago,
        companyId
      ).catch(e => {
        console.log(e, 'e');
      });
    }
  };

  const handleAmountClick = (id, amount) => {
    changeAdditionalFeeAmountInLocalStorage(id, amount);
    dispatch(changeAdditionalFeeAmount(id, amount));
  };

  return (
    <section className="product-basket">
      <h3 className="label-l spacer-bottom-24">Dodane produkty</h3>
      <AnimatePresence>
        {additionalFeeList.map(product => {
          const {
            amount,
            defaultPrice,
            description,
            imageUrl,
            maxQuantity,
            minQuantity,
            name,
            possibleSideOrderId,
          } = product;

          return (
            <motion.div
              animate={{
                opacity: 1,
                height: 'auto',
                marginBottom: 24,
                paddingBottom: 24,
              }}
              className="product-basket__product"
              exit={{
                opacity: 0,
                height: 0,
                marginBottom: -20,
                paddingBottom: -20,
              }}
              initial={{
                opacity: 0,
                height: 0,
                marginBottom: 24,
                paddingBottom: 24,
              }}
              key={possibleSideOrderId}
              transition={{ duration: 0.2 }}
            >
              <div className="product-basket__wrapper">
                <div
                  className={classNamesUtil('product-basket__image', {
                    'product-basket__image--empty': !imageUrl,
                  })}
                >
                  {imageUrl ? (
                    <img alt="" src={imageUrl} />
                  ) : (
                    <EmptyImageIcon className="empty-image-icon" />
                  )}
                </div>
                <div className="product-basket__header-mobile">
                  <h6>{name}</h6>
                  <div className="display-flex flex-direction-column">
                    <span>{parsePrice(defaultPrice * amount)} zł</span>
                    <div className="body-m spacer-vertical-8">jednorazowo</div>
                  </div>
                </div>
              </div>
              <div className="width-100-per">
                <div className="product-basket__header-desktop">
                  <h6>{name}</h6>
                  <div className="display-flex flex-direction-column align-items-end">
                    <span>{parsePrice(defaultPrice * amount)} zł</span>
                    <div className="body-m spacer-vertical-8">jednorazowo</div>
                  </div>
                </div>
                <div className="product-basket__description">
                  <div>
                    <p className="spacer-bottom-24 spacer-right-20">
                      {description}
                    </p>
                    <div
                      className="product-basket__button"
                      onClick={() => handleRemoveClick(product)}
                      onKeyDown={() => handleRemoveClick(product)}
                      role="button"
                      tabIndex="0"
                    >
                      <FontAwesomeIcon
                        className="color-gray-400 spacer-right-8"
                        icon={faTrash}
                      />
                      Usuń produkt
                    </div>
                  </div>
                  <div className="display-flex">
                    <div className="product-basket__amount">
                      <DaysAmount
                        days={amount}
                        decrease={() => {
                          if (amount > (minQuantity || 1)) {
                            handleAmountClick(
                              product.possibleSideOrderId,
                              amount - 1
                            );
                          }
                        }}
                        increase={() => {
                          if (maxQuantity && amount < maxQuantity) {
                            handleAmountClick(
                              product.possibleSideOrderId,
                              amount + 1
                            );
                          }

                          if (!maxQuantity) {
                            handleAmountClick(
                              product.possibleSideOrderId,
                              amount + 1
                            );
                          }
                        }}
                        withoutBorders
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </section>
  );
};

ProductBasket.propTypes = {
  additionalFeeList: PropTypes.array,
};

export default ProductBasket;
